import { Injectable } from '@angular/core';
import Toastify from 'toastify-js';

export interface ToastOptions {
  duration?: number;
  position?: 'top-right' | 'top-left' | 'bottom-right' | 'bottom-left' | 'top-center' | 'bottom-center';
  showIcon?: boolean;
  action?: {
    text: string;
    onClick: () => void;
  };
  customClass?: string;
}

@Injectable({
  providedIn: 'root'
})
export class ToastService {

  private getPositionClasses(position: string): any {
    const positions: any = {
      'top-right': { gravity: 'top', position: 'right' },
      'top-left': { gravity: 'top', position: 'left' },
      'bottom-right': { gravity: 'bottom', position: 'right' },
      'bottom-left': { gravity: 'bottom', position: 'left' },
      'top-center': { gravity: 'top', position: 'center' },
      'bottom-center': { gravity: 'bottom', position: 'center' }
    };
    return positions[position] || positions['top-right'];
  }

  private getBaseClasses(): string {
    return `
      rounded-lg shadow-lg border-l-4 p-1 max-w-sm
      transform transition-all duration-300 backdrop-blur-sm
      bg-white/95 dark:bg-gray-900/95
      border
      animate-in slide-in-from-right-full
    `;
  }

  private getTypeClasses(type: string): string {
    const typeClasses: any = {
      success: 'border-l-green-500 bg-green-50/80 dark:bg-green-900/90 text-green-900 dark:text-green-100',
      error: 'border-l-red-500 bg-red-50/80 dark:bg-red-900/90 text-red-900 dark:text-red-100',
      warning: 'border-l-yellow-500 bg-yellow-50/80 dark:bg-yellow-900/90 text-yellow-900 dark:text-yellow-100',
      info: 'border-l-blue-500 bg-blue-50/80 dark:bg-blue-900/90 text-blue-900 dark:text-blue-100',
      default: 'border-l-gray-500 bg-gray-50/80 dark:bg-gray-800/90 text-gray-900 dark:text-gray-100'
    };

    return typeClasses[type] || typeClasses.default;
  }

  private getIcon(type: string): string {
    const icons: any = {
      success: `
        <svg class="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
          <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clip-rule="evenodd"/>
        </svg>
      `,
      error: `
        <svg class="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
          <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clip-rule="evenodd"/>
        </svg>
      `,
      warning: `
        <svg class="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
          <path fill-rule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clip-rule="evenodd"/>
        </svg>
      `,
      info: `
        <svg class="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
          <path fill-rule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clip-rule="evenodd"/>
        </svg>
      `
    };

    return icons[type] || icons.info;
  }

  show(message: string, type: 'success' | 'error' | 'warning' | 'info' | 'default' = 'default', options: ToastOptions = {}) {
    const { duration = 5000, position = 'top-right', showIcon = true, action, customClass = '' } = options;
    const positionConfig = this.getPositionClasses(position);

    const toastElement = document.createElement('div');
    toastElement.className = ` ${this.getBaseClasses()} ${this.getTypeClasses(type)} ${customClass}`;

    toastElement.innerHTML = `
      <div class="flex items-start gap-3">
        ${showIcon ? `
          <div class="shrink-0 mt-0.5">
            <div class="[&>svg]:w-5 [&>svg]:h-5">
              ${this.getIcon(type)}
            </div>
          </div>
        ` : ''}

        <div class="flex-1 min-w-0">
          <p class="text-xs font-medium leading-5">${message}</p>

          ${action ? `
            <div class="mt-2">
              <button
                onclick="this.dispatchEvent(new CustomEvent('toast-action', { bubbles: true }))"
                class="text-xs font-medium underline hover:no-underline transition-all"
              >
                ${action.text}
              </button>
            </div>
          ` : ''}
        </div>

        <button
          onclick="this.dispatchEvent(new CustomEvent('toast-close', { bubbles: true }))"
          class="shrink-0 ml-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
        >
          <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/>
          </svg>
        </button>
      </div>
    `;

    const toast = Toastify({
      node: toastElement,
      duration: duration,
      close: false,
      ...positionConfig,
    });

    toastElement.addEventListener('toast-close', () => {
      toast.hideToast();
    });

    if (action) {
      toastElement.addEventListener('toast-action', () => {
        action.onClick();
        toast.hideToast();
      });
    }

    toast.showToast();
    return toast;
  }

  success(message: string, options?: Omit<ToastOptions, 'customClass'>) {
    return this.show(message, 'success', options);
  }

  error(message: string, options?: Omit<ToastOptions, 'customClass'>) {
    return this.show(message, 'error', options);
  }

  warning(message: string, options?: Omit<ToastOptions, 'customClass'>) {
    return this.show(message, 'warning', options);
  }

  info(message: string, options?: Omit<ToastOptions, 'customClass'>) {
    return this.show(message, 'info', options);
  }

  progressive(message: string, type: 'success' | 'error' | 'warning' | 'info' = 'info', duration: number = 10000) {
    const progressId = `progress-${Date.now()}`;

    const toastElement = document.createElement('div');
    toastElement.className = `${this.getBaseClasses()} ${this.getTypeClasses(type)}`;

    toastElement.innerHTML = `
      <div class="flex items-start gap-3">
        <div class="shrink-0 mt-0.5">
          <div class="[&>svg]:w-5 [&>svg]:h-5">
            ${this.getIcon(type)}
          </div>
        </div>

        <div class="flex-1 min-w-0">
          <p class="text-sm font-medium leading-5 mb-2">${message}</p>
          <div class="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-1">
            <div
              id="${progressId}"
              class="h-1 rounded-full transition-all duration-100 ease-linear ${this.getProgressColor(type)}"
              style="width: 100%"
            ></div>
          </div>
        </div>

        <button
          onclick="this.dispatchEvent(new CustomEvent('toast-close', { bubbles: true }))"
          class="shrink-0 ml-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
        >
          <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/>
          </svg>
        </button>
      </div>
    `;

    const toast = Toastify({
      node: toastElement,
      duration: duration,
      close: false,
      gravity: 'top',
      position: 'right'
    });

    const progressBar = document.getElementById(progressId) as HTMLElement;
    if (progressBar) {
      let width = 100;
      const interval = setInterval(() => {
        width -= (100 / (duration / 100));
        progressBar.style.width = width + '%';
        if (width <= 0) {
          clearInterval(interval);
        }
      }, 100);
    }

    toastElement.addEventListener('toast-close', () => {
      toast.hideToast();
    });

    toast.showToast();
    return toast;
  }

  private getProgressColor(type: string): string {
    const colors: any = {
      success: 'bg-green-500',
      error: 'bg-red-500',
      warning: 'bg-yellow-500',
      info: 'bg-blue-500',
      default: 'bg-gray-500'
    };
    return colors[type] || colors.default;
  }

  custom(message: string, classes: string, duration: number = 5000) {
    const toastElement = document.createElement('div');
    toastElement.className = `${this.getBaseClasses()} ${classes}`;

    toastElement.innerHTML = `
      <div class="flex items-center gap-3">
        <div class="flex-1">
          <p class="text-sm font-medium">${message}</p>
        </div>
        <button
          onclick="this.dispatchEvent(new CustomEvent('toast-close', { bubbles: true }))"
          class="shrink-0 text-current opacity-70 hover:opacity-100 transition-opacity"
        >
          <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/>
          </svg>
        </button>
      </div>
    `;

    const toast = Toastify({
      node: toastElement,
      duration: duration,
      close: false,
      gravity: 'top',
      position: 'right'
    });

    toastElement.addEventListener('toast-close', () => {
      toast.hideToast();
    });

    toast.showToast();
    return toast;
  }
}
